import { useEffect, useRef, useState, type KeyboardEvent } from 'react';
import {
  CARRIERS,
  CARRIER_OTHER,
  CARRIER_OTHER_MAX,
  DRIVER_NAME_MAX,
  SHIPPING_METHODS,
  instantOfLabDay,
  resolveCarrier,
  shippingFieldErrors,
  type DeliverTo as DeliverToValue,
  type Shipping,
  type ShippingMethod,
} from '@/domain';
import type { ShippingChanges } from '@/application/ports/JobOrderRepository';
import { strings } from '@/shared/strings';
import { formatDay } from '@/shared/format/instant';
import { ConfirmSheet } from '@/ui/primitives/ConfirmSheet';
import { modalStyles } from '@/ui/primitives/Modal';
import { DeliverTo } from './DeliverTo';

/** What a new dispatch sends. Only what was given; an absent code is a code that comes later. */
export interface DispatchInput {
  method: ShippingMethod;
  carrier?: string;
  /** The name typed beside `Other`. The server resolves the two into one. */
  carrierOther?: string;
  trackingCode?: string;
  driverName?: string;
  note?: string;
  dispatchedAt?: string;
}

interface CommonProps {
  jobOrderNo: string;
  busy: boolean;
  onCancel: () => void;
  /**
   * Where the case goes, as the case carries it: the doctor's clinic, address
   * and number. Absent for a reader without seeIdentity, null for a case with
   * no doctor's record, and nothing is drawn for either.
   */
  deliverTo?: DeliverToValue | null;
  /** The doctor's name, the first line of that label: the dialog names the case and nobody else does. */
  doctorName?: string | null;
}

export type DispatchDialogProps = CommonProps &
  (
    | { mode?: 'dispatch'; onConfirm: (input: DispatchInput) => void }
    /** A case already on its way, prefilled, saving only what changed. */
    | { mode: 'edit'; shipping: Shipping; onSave: (changes: ShippingChanges) => void }
  );

const METHOD_LABEL: Record<ShippingMethod, string> = {
  courier: strings.shipping.methodCourier,
  lab: strings.shipping.methodLab,
};

const dayOrEmpty = (instant: string | null) => (instant === null ? '' : formatDay(instant));

/**
 * Dispatch, and correcting a dispatch. Section 5.2, item 5 of section 17, and
 * the request of 13 September 2026, section 1.6.
 *
 * The case leaves by courier or in the lab's van, and the person says which.
 * A courier's waybill code is typed by hand and is NEVER generated, never
 * validated against a carrier's format and never auto filled; it may be left
 * empty, because the courier sends it the next morning. A lab delivery has no
 * code at all, so the field is not on screen rather than disabled: a field that
 * cannot ever be filled should not be there.
 *
 * The hand-over date defaults to today, so a case that left at five and was
 * entered at six the next morning is not billed a day late.
 *
 * Since the request of 5 October 2026 (third), section 2, the dialog is the
 * confirmation and not a form in front of one: built on the shared
 * `ConfirmSheet`, with a question that follows the route and the courier as
 * they are chosen. A field that is wrong keeps the sheet where it is, with the
 * reason. Enter in a field answers as the button does, and the two send once
 * between them.
 *
 * Since the request of 6 October 2026 (fifth), section 2, the dialog says
 * where the case is going, above the route: the doctor's clinic, the address
 * in its country's format and the number to ring, for whoever may see them.
 * It is what the desk writes on the parcel, so it is here at the moment the
 * parcel is handed over, and again when the dispatch is corrected.
 *
 * Since the lab's request of 22 October 2026 the route itself is corrected
 * too: Courier and Lab delivery are offered in the edit as at dispatch, and a
 * case moved to a courier names its courier in the same save.
 */
export function DispatchDialog(props: DispatchDialogProps) {
  const { jobOrderNo, busy, onCancel, deliverTo, doctorName } = props;
  const editing = props.mode === 'edit' ? props.shipping : null;
  const today = formatDay(new Date().toISOString());

  const [method, setMethod] = useState<ShippingMethod>(editing?.method ?? 'courier');
  /*
   * A case already on its way carries the resolved NAME, not the choice, so a
   * carrier that is not one of the four reopens as `Other` with its name in the
   * box beside it. Section 8.2 of the request of 16 September 2026.
   */
  const named = editing?.carrier ?? null;
  const fromTheList = named !== null && (CARRIERS as readonly string[]).includes(named);
  const [carrier, setCarrier] = useState<string>(
    named === null ? CARRIERS[0] : fromTheList ? named : CARRIER_OTHER,
  );
  const [carrierOther, setCarrierOther] = useState(fromTheList || named === null ? '' : named);
  const [code, setCode] = useState(editing?.trackingCode ?? '');
  const [driver, setDriver] = useState(editing?.driverName ?? '');
  const [note, setNote] = useState(editing?.note ?? '');
  const [handedOver, setHandedOver] = useState(editing === null ? today : dayOrEmpty(editing.dispatchedAt));
  const [trackedOn, setTrackedOn] = useState(dayOrEmpty(editing?.trackedAt ?? null));
  const [receivedOn, setReceivedOn] = useState(dayOrEmpty(editing?.deliveredAt ?? null));
  const [receivedBy, setReceivedBy] = useState(editing?.receivedBy ?? '');
  const [error, setError] = useState<string | null>(null);

  const courier = method === 'courier';

  // Enter does not pass through the sheet's own lock, so the dialog keeps one of its own.
  const sent = useRef(false);
  useEffect(() => {
    if (!busy) sent.current = false;
  }, [busy]);

  function confirm(): boolean {
    if (busy || sent.current) return true;
    const now = new Date().toISOString();
    const trimmedCode = code.trim();
    const instant = (day: string, was: string | null) =>
      editing !== null && day === dayOrEmpty(was) ? was : instantOfLabDay(day, today, now);

    // A cleared date is read as today rather than as no hand-over at all.
    const handedDay = handedOver === '' ? today : handedOver;
    const dispatchedAt = instant(handedDay, editing?.dispatchedAt ?? null);
    const deliveredAt = receivedOn === '' ? null : instant(receivedOn, editing?.deliveredAt ?? null);

    const problems = shippingFieldErrors({
      method,
      carrier: courier ? carrier : null,
      carrierOther,
      trackingCode: courier && trimmedCode !== '' ? trimmedCode : null,
      dispatchedAt,
      deliveredAt,
    });
    const first = Object.values(problems)[0];
    if (first !== undefined) {
      setError(first);
      return false;
    }

    if (props.mode !== 'edit') {
      sent.current = true;
      props.onConfirm({
        method,
        ...(courier ? { carrier } : {}),
        ...(courier && carrier === CARRIER_OTHER ? { carrierOther: carrierOther.trim() } : {}),
        ...(courier && trimmedCode !== '' ? { trackingCode: trimmedCode } : {}),
        ...(!courier && driver.trim() !== '' ? { driverName: driver.trim() } : {}),
        ...(note.trim() === '' ? {} : { note: note.trim() }),
        // Today is now, and now is what the server stamps without being told.
        ...(handedDay === today ? {} : { dispatchedAt: instantOfLabDay(handedDay, today, now) }),
      });
      return true;
    }

    const shipping = props.shipping;
    const orNull = (value: string) => (value.trim() === '' ? null : value.trim());
    const changes: ShippingChanges = {};
    const switching = method !== shipping.method;
    if (switching) changes.method = method;
    // Compared on the resolved name, which is what the order carries: reopening
    // an `Other` case and saving something else is not a change to `Other`.
    const chosen = resolveCarrier(carrier, carrierOther);
    if (courier && chosen !== null && chosen !== shipping.carrier) {
      changes.carrier = carrier;
      if (carrier === CARRIER_OTHER) changes.carrierOther = carrierOther.trim();
    }
    if (courier && orNull(code) !== shipping.trackingCode) changes.trackingCode = orNull(code);
    if (!courier && orNull(driver) !== shipping.driverName) changes.driverName = orNull(driver);
    if (orNull(note) !== shipping.note) changes.note = orNull(note);
    if (dispatchedAt !== shipping.dispatchedAt && dispatchedAt !== null) changes.dispatchedAt = dispatchedAt;
    if (
      !switching &&
      shipping.trackedAt !== null &&
      trackedOn !== dayOrEmpty(shipping.trackedAt) &&
      trackedOn !== ''
    ) {
      changes.trackedAt = instantOfLabDay(trackedOn, today, now);
    }
    if (shipping.deliveredAt !== null) {
      if (deliveredAt !== shipping.deliveredAt) changes.deliveredAt = deliveredAt;
      if (orNull(receivedBy) !== shipping.receivedBy) changes.receivedBy = orNull(receivedBy);
    }

    if (Object.keys(changes).length === 0) {
      setError(strings.shipping.nothingChanged);
      return false;
    }
    sent.current = true;
    props.onSave(changes);
    return true;
  }

  const onEnter = (event: KeyboardEvent) => {
    if (event.key === 'Enter') confirm();
  };

  const statement =
    editing !== null
      ? method === editing.method
        ? strings.confirm.saveShipping(jobOrderNo)
        : strings.confirm.saveShippingMethod(jobOrderNo, courier ? resolveCarrier(carrier, carrierOther) : null)
      : courier
        ? strings.confirm.dispatch(jobOrderNo, resolveCarrier(carrier, carrierOther))
        : strings.confirm.dispatchByLab(jobOrderNo);

  return (
    <ConfirmSheet
      title={`${editing === null ? strings.dispatch.title : strings.shipping.editTitle} ${jobOrderNo}`}
      statement={statement}
      confirmLabel={editing === null ? strings.dispatch.confirm : strings.shipping.editSave}
      cancelLabel={strings.dispatch.cancel}
      closeLabel={strings.sheet.close}
      onConfirm={confirm}
      onCancel={onCancel}
      busy={busy}
    >
      {editing === null && (
        <div className={modalStyles.field}>
          <span className={modalStyles.hint}>{strings.dispatch.lead}</span>
        </div>
      )}

      <DeliverTo deliverTo={deliverTo} name={doctorName} className={modalStyles.field} />

      <div className={modalStyles.field}>
        <span className={modalStyles.label}>{strings.dispatch.method}</span>
        {/* Chosen at dispatch, and corrected in the edit since the lab's request of 22 October 2026. */}
        <div className={modalStyles.choices} role="group" aria-label={strings.dispatch.method}>
          {SHIPPING_METHODS.map((option, index) => (
            <button
              key={option}
              type="button"
              className={method === option ? modalStyles.on : undefined}
              aria-pressed={method === option}
              // Where focus opens, as it did before the sheet had a close button above it.
              data-autofocus={index === 0 ? '' : undefined}
              onClick={() => {
                setMethod(option);
                setError(null);
              }}
            >
              {METHOD_LABEL[option]}
            </button>
          ))}
        </div>
      </div>

      {courier ? (
        <>
          <div className={modalStyles.field}>
            <span className={modalStyles.label}>{strings.dispatch.carrier}</span>
            <div className={modalStyles.choices} role="group" aria-label={strings.dispatch.carrier}>
              {CARRIERS.map((option) => (
                <button
                  key={option}
                  className={carrier === option ? modalStyles.on : undefined}
                  aria-pressed={carrier === option}
                  onClick={() => {
                    setCarrier(option);
                    setError(null);
                  }}
                >
                  {option}
                </button>
              ))}
            </div>
          </div>

          {/*
            Revealed by `Other`, and required by it. The rest of the field stays
            a closed list on purpose: the four are what the lab uses daily, and
            typing DHL four different ways is how a carrier report becomes
            useless. Section 8.2 of the request of 16 September 2026.
          */}
          {carrier === CARRIER_OTHER && (
            <div className={modalStyles.field}>
              <label className={modalStyles.label} htmlFor="dispatch-carrier-other">
                {strings.shipping.otherCarrier}
              </label>
              <input
                id="dispatch-carrier-other"
                className={modalStyles.input}
                value={carrierOther}
                maxLength={CARRIER_OTHER_MAX}
                placeholder={strings.shipping.otherCarrierPlaceholder}
                autoComplete="off"
                onChange={(event) => {
                  setCarrierOther(event.target.value);
                  setError(null);
                }}
                onKeyDown={onEnter}
              />
            </div>
          )}

          <div className={modalStyles.field}>
            <label className={modalStyles.label} htmlFor="dispatch-code">
              {strings.dispatch.code}
            </label>
            <input
              id="dispatch-code"
              className={modalStyles.input}
              value={code}
              placeholder={strings.dispatch.codePlaceholder}
              autoComplete="off"
              onChange={(event) => {
                setCode(event.target.value);
                setError(null);
              }}
              onKeyDown={onEnter}
            />
          </div>
        </>
      ) : (
        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor="dispatch-driver">
            {strings.dispatch.driver}
          </label>
          <input
            id="dispatch-driver"
            className={modalStyles.input}
            value={driver}
            maxLength={DRIVER_NAME_MAX}
            placeholder={strings.dispatch.driverPlaceholder}
            autoComplete="off"
            onChange={(event) => setDriver(event.target.value)}
            onKeyDown={onEnter}
          />
        </div>
      )}

      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor="dispatch-handed-over">
          {strings.shipping.handedOverOn}
        </label>
        <input
          id="dispatch-handed-over"
          type="date"
          className={modalStyles.input}
          value={handedOver}
          max={today}
          required
          onChange={(event) => {
            setHandedOver(event.target.value);
            setError(null);
          }}
        />
      </div>

      {editing !== null && courier && editing.trackedAt !== null && (
        <div className={modalStyles.field}>
          <label className={modalStyles.label} htmlFor="dispatch-tracked-on">
            {strings.shipping.trackedOn}
          </label>
          <input
            id="dispatch-tracked-on"
            type="date"
            className={modalStyles.input}
            value={trackedOn}
            max={today}
            onChange={(event) => setTrackedOn(event.target.value)}
          />
        </div>
      )}

      {editing !== null && editing.deliveredAt !== null && (
        <>
          <div className={modalStyles.field}>
            <label className={modalStyles.label} htmlFor="dispatch-received-on">
              {strings.shipping.receivedOn}
            </label>
            {/* Emptied, it takes the received mark back: somebody ticked the wrong row. */}
            <input
              id="dispatch-received-on"
              type="date"
              className={modalStyles.input}
              value={receivedOn}
              max={today}
              onChange={(event) => {
                setReceivedOn(event.target.value);
                setError(null);
              }}
            />
          </div>
          <div className={modalStyles.field}>
            <label className={modalStyles.label} htmlFor="dispatch-received-by">
              {strings.shipping.receivedByLabel}
            </label>
            <input
              id="dispatch-received-by"
              className={modalStyles.input}
              value={receivedBy}
              autoComplete="off"
              onChange={(event) => setReceivedBy(event.target.value)}
            />
          </div>
        </>
      )}

      <div className={modalStyles.field}>
        <label className={modalStyles.label} htmlFor="dispatch-note">
          {strings.dispatch.noteLabel}
        </label>
        <input
          id="dispatch-note"
          className={modalStyles.input}
          value={note}
          placeholder={strings.dispatch.notePlaceholder}
          onChange={(event) => setNote(event.target.value)}
        />
      </div>

      {error !== null && (
        <p className={modalStyles.error} role="alert">
          {error}
        </p>
      )}
    </ConfirmSheet>
  );
}
